import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { CampaignLaunchAnswers } from 'fym-shared/src/music24/campaignLaunch';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { CALENDAR_LAUNCH_PLACE } from '../releases/plan/rollout/launch/launchModel';
import { usePostsLaunch, type LaunchRun } from '../releases/plan/rollout/launch/usePostsLaunch';
import type { CalendarPost } from '../types';
import { isQuotaError, postErrorMessage } from './calendarPostErrors';

/**
 * "Schedule all drafts of this day" (owner decision 2026-10-11): the flow of the plan
 * (`usePostsLaunch`). When a platform needs the user, the questions dialog asks one time; else the
 * drafts are scheduled at once. The drafts that can go out are scheduled, and the summary says
 * that all are scheduled or names each draft that stays a draft.
 */
export function useCalendarLaunch(accountId: number) {
  const intl = useIntl();
  const { showError } = useShowToast();
  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const run = useCallback<LaunchRun>(
    async (_place, stage, work) => {
      setBusy(true);
      setError(undefined);
      try {
        await work();
        return true;
      } catch (caught: unknown) {
        // The base query opens the upgrade prompt for a 402 by itself.
        if (!isQuotaError(caught)) {
          const sentence = postErrorMessage(intl, caught);
          // An open dialog shows the sentence itself; else the Calendar says it as each other write.
          if (stage === 'answer') {
            setError(sentence);
          } else {
            showError(sentence);
          }
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [intl, showError]
  );

  const { launch, answerLaunch, cancelLaunch, dismissLaunchOutcome, pendingLaunch, launchOutcome } = usePostsLaunch({
    accountId,
    run,
  });

  const schedule = useCallback(
    (postUuids: string[]) => {
      launch(CALENDAR_LAUNCH_PLACE, postUuids).catch(console.error);
    },
    [launch]
  );
  const scheduleDrafts = useCallback((drafts: CalendarPost[]) => schedule(drafts.map((post) => post.uuid)), [schedule]);
  const answer = useCallback(
    (answers: CampaignLaunchAnswers) => {
      answerLaunch(answers).catch(console.error);
    },
    [answerLaunch]
  );
  const cancel = useCallback(() => {
    setError(undefined);
    cancelLaunch();
  }, [cancelLaunch]);

  // One object while nothing changes, so the memoised parts do not draw again on every render.
  return useMemo(
    () => ({
      isBusy,
      error,
      pending: pendingLaunch,
      outcome: launchOutcome,
      schedule,
      scheduleDrafts,
      answer,
      cancel,
      dismiss: dismissLaunchOutcome,
    }),
    [answer, cancel, dismissLaunchOutcome, error, isBusy, launchOutcome, pendingLaunch, schedule, scheduleDrafts]
  );
}

export type CalendarLaunch = ReturnType<typeof useCalendarLaunch>;
